import React, { useState, useEffect } from 'react';
import { Card, DatePicker, Row, Col, Statistic, Table, Typography, Button, Space, Tag } from 'antd';
import { Column } from '@ant-design/plots';
import { 
  CalendarOutlined, 
  TeamOutlined, 
  ShoppingOutlined, 
  FileSearchOutlined,
  ClearOutlined 
} from '@ant-design/icons';
import axios from "axios";
import constants from "../../constant";
import Loader from '../../utils/Loader';

const { RangePicker } = DatePicker;
const { Title } = Typography;

const AnalyticsDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState(null);
  const [data, setData] = useState({
    totalLeads: 0,
    pocTotalLeads: [],
    leadCountBySource: [],
    productCount: [],
    leadTypeCount: [],
    conversionByLeadsourceWithPoc: [] // Make sure this is initialized
  });

  const { Text } = Typography;

  const handleDateRangeChange = async (dates, dateStrings) => {
    const [startDate, endDate] = dateStrings;
    setDateRange(dates);
    if (startDate && endDate) {
      fetchAnalytics({ startDate, endDate });
    }
  };

  const handleClearRange = () => {
    setDateRange(null);
    fetchAnalytics();
  };

  const fetchAnalytics = async (dateRange = null) => {
    setLoading(true);
    try {
      let url = `${constants.DOMAIN}/api/v1/marketing/analytics`;
      if (dateRange) {
        url += `?startDate=${dateRange.startDate}&endDate=${dateRange.endDate}`;
      }
      const res = await axios.get(url);
      if (res.data?.data) {
        setData(res.data.data);
      }
    } catch (error) {
      console.error("Failed to fetch analytics:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalytics();
  }, []);


  const columnsConversion = [
    {
      title: 'Source',
      dataIndex: '_id',
      key: '_id',
      sorter: (a, b) => a._id.localeCompare(b._id), // Fixed to use _id instead of source
    },
    {
      title: 'Total Count',
      dataIndex: 'totalCount',
      key: 'totalCount',
      sorter: (a, b) => a.totalCount - b.totalCount,
      render: (count) => <Text strong>{count}</Text>,
    },
    {
      title: 'POC Data',
      dataIndex: 'pocDetails',
      key: 'pocDetails',
      render: (pocDetails) => (
        <>
          {pocDetails && pocDetails.map((poc, index) => (
            <Tag color={getPocColor(poc.pocName)} key={index} className="mb-1">
              {poc.pocName || 'Unknown'} ({poc.count }) {"( ₹" + poc.amount + " )"}
            </Tag>
          ))}
        </>
      ),
    },
    {
      title: 'Total Amount',
      dataIndex: 'totalAmount',
      key: 'totalAmount',
      render: (_, record) => {
        // Calculate total amount from pocDetails
        const total = record.pocDetails?.reduce((sum, poc) => sum + (poc.amount || 0), 0) || 0;
        return (
          <Text type="success">₹{total.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</Text>
        );
      },
      sorter: (a, b) => {
        const totalA = a.pocDetails?.reduce((sum, poc) => sum + (poc.amount || 0), 0) || 0;
        const totalB = b.pocDetails?.reduce((sum, poc) => sum + (poc.amount || 0), 0) || 0;
        return totalA - totalB;
      },
    },
  ];

  // Function to assign consistent colors to POCs
  const getPocColor = (pocName) => {
    if (!pocName) return 'gray';
    
    const colorMap = {
      'Swati': 'magenta',
      'Vishal': 'blue',
      'Prashal': 'green',
      'Unknown': 'gray'
    };
    return colorMap[pocName] || 'purple';
  };

  const summary = (pageData) => {
    let totalLeads = 0;
    let totalRevenue = 0;

    pageData.forEach((record) => {
      totalLeads += record.totalCount || 0;
      const recordTotal = record.pocDetails?.reduce((sum, poc) => sum + (poc.amount || 0), 0) || 0;
      totalRevenue += recordTotal;
    });

    return (
      <>
        <Table.Summary.Row>
          <Table.Summary.Cell index={0}>
            <Text strong>Total</Text>
          </Table.Summary.Cell>
          <Table.Summary.Cell index={1}>
            <Text strong>{totalLeads}</Text>
          </Table.Summary.Cell>
          <Table.Summary.Cell index={2}></Table.Summary.Cell>
          <Table.Summary.Cell index={3}>
            <Text strong type="success">
              ₹{totalRevenue.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
            </Text>
          </Table.Summary.Cell>
        </Table.Summary.Row>
      </>
    );
  };

  const pocColumns = [
    {
      title: 'POC Name',
      dataIndex: 'pocName',
      key: 'pocName',
    },
    {
      title: 'Total Leads',
      dataIndex: 'totalLeads',
      key: 'totalLeads',
      sorter: (a, b) => a.totalLeads - b.totalLeads,
    },
    {
      title: 'Leads in Range',
      dataIndex: 'leadsInRange',
      key: 'leadsInRange',
      sorter: (a, b) => a.leadsInRange - b.leadsInRange,
    },
  ];

  const chartConfig = {
    height: 300,
    isGroup: true,
    columnStyle: {
      radius: [4, 4, 0, 0],
    },
    label: {
      style: {
        fill: '#FFFFFF',
        opacity: 0.6,
      },
    },
    xAxis: {
      label: {
        autoHide: true,
        autoRotate: false,
      },
    },
  };

  if (loading && !data.totalLeads) {
    return <Loader />;
  }

  const getTotalProducts = () => {
    return data.productCount
  };

  return (
    <div style={{ padding: 24, background: '#f0f2f5', minHeight: '100vh' }}>
      {/* Date Filter */}
      <Card 
        title={
          <span>
            <CalendarOutlined style={{ marginRight: 8 }} />
            Date Range Filter
          </span>
        }
        style={{ marginBottom: 24 }}
      >
        <Space>
          <RangePicker 
            value={dateRange}
            onChange={handleDateRangeChange}
            style={{ width: 300 }}
          />
          <Button 
            icon={<ClearOutlined />} 
            onClick={handleClearRange}
            disabled={!dateRange}
          >
            Clear Range
          </Button>
        </Space>
      </Card>

      {/* Statistics Summary */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={8}>
          <Card>
            <Statistic
              title="Total Leads"
              value={data.totalLeads || 0}
              prefix={<TeamOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={8}>
          <Card>
            <Statistic
              title="Total Products"
              value={getTotalProducts()}
              prefix={<ShoppingOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={8}>
          <Card>
            <Statistic
              title="Total Sources"
              value={data.sourceCount|| 0}
              prefix={<FileSearchOutlined />}
            />
          </Card>
        </Col>
      </Row>

      {/* POC Performance Table */}
      <Card 
        title={
          <Typography.Text strong>
            POC Performance
          </Typography.Text>
        }
        style={{ marginBottom: 24 }}
      >
        <Table 
          loading={loading}
          columns={pocColumns}
          dataSource={data.pocTotalLeads.filter(
            (item) => item.pocName
          ) || []}
          rowKey="pocName"
          pagination={false}
        />
      </Card>

      {/* Charts Section */}
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={24}>
          <Card 
            title={
              <Typography.Text strong>
                Lead Sources Distribution
              </Typography.Text>
            }
          >
            {data.leadCountBySource?.length > 0 && (
              <Column 
                {...chartConfig}
                data={data.leadCountBySource}
                xField="_id"
                yField="count"
                meta={{
                  _id: { alias: 'Source' },
                  count: { alias: 'Count' },
                }}
              />
            )}
          </Card>
        </Col>
        <Col xs={24} lg={24}>
          <Card 
            title={
              <Typography.Text strong>
                Order By Sources
              </Typography.Text>
            }
          >
            {data.conversionByLeadsourceWithPoc?.length > 0 ? (
             <Table 
             columns={columnsConversion} 
             dataSource={data.conversionByLeadsourceWithPoc} // Fixed: use data.conversionByLeadsourceWithPoc instead of dateRange.conversionByLeadsourceWithPoc
             pagination={false}
             summary={summary}
             bordered
             size="middle"
             title={() => <Text strong style={{ fontSize: 16 }}>Lead Source Conversion Summary</Text>}
           />
            ) :<>No Data</>}
          </Card>
        </Col>
        <Col xs={24} lg={24}>
          <Card 
            title={
              <Typography.Text strong>
                Order By Procuct
              </Typography.Text>
            }
          >
            {data.conversionByProduct?.length > 0 ? (
             <Table 
             columns={columnsConversion} 
             dataSource={data.conversionByProduct} // Fixed: use data.conversionByLeadsourceWithPoc instead of dateRange.conversionByLeadsourceWithPoc
             pagination={false}
             summary={summary}
             bordered
             size="middle"
             title={() => <Text strong style={{ fontSize: 16 }}>Lead Source Conversion Summary</Text>}
           />
            ) :<>No Data</>}
          </Card>
        </Col>
        <Col xs={24}>
          <Card 
            title={
              <Typography.Text strong>
                Lead Status Distribution
              </Typography.Text>
            }
          >
            {data.leadTypeCount?.length > 0 && (
              <Column 
                {...chartConfig}
                data={data.leadTypeCount}
                xField="_id"
                yField="count"
                meta={{
                  _id: { alias: 'Status' },
                  count: { alias: 'Count' },
                }}
              />
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default AnalyticsDashboard;